import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listBranches } from '../api/branches';
import { useAuth } from './authContext';
import { BranchContext, BranchContextValue } from './branchContext';
import { GLOBAL_BRANCH_SCOPE_ROLES } from '../utils/roleScope';

const STORAGE_KEY = 'imc-selected-branch';

/**
 * Only globally-scoped roles (Super Admin, Lead Manager) can actually
 * change what branch they see — every other role is branch-scoped
 * server-side regardless of any branchId filter sent (see docs/RBAC.md),
 * so the selector stays inert (branches: [], selection always undefined)
 * for everyone else rather than offering a control that silently does
 * nothing.
 */
export const BranchProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const isGlobalScope = !!user && GLOBAL_BRANCH_SCOPE_ROLES.includes(user.role.name);

  const [selectedBranchId, setSelectedBranchIdState] = useState<number | undefined>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? Number(stored) : undefined;
  });

  const branchesQuery = useQuery({
    queryKey: ['branches', 'selector'],
    queryFn: () => listBranches(true),
    enabled: isGlobalScope,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!isGlobalScope) setSelectedBranchIdState(undefined);
  }, [isGlobalScope]);

  const setSelectedBranchId = (branchId: number | undefined) => {
    setSelectedBranchIdState(branchId);
    if (branchId) localStorage.setItem(STORAGE_KEY, String(branchId));
    else localStorage.removeItem(STORAGE_KEY);
  };

  const value = useMemo<BranchContextValue>(
    () => ({
      branches: isGlobalScope ? (branchesQuery.data ?? []) : [],
      selectedBranchId: isGlobalScope ? selectedBranchId : undefined,
      setSelectedBranchId,
    }),
    [isGlobalScope, branchesQuery.data, selectedBranchId],
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};
